<template>
  <v-dialog v-model="show" max-width="500" persistent>
    <v-card>
      <v-card-title class="text-h5 text-center py-4">
        <v-icon color="warning" class="me-2">mdi-lock-alert</v-icon>
        {{ $t('auth.changePassword') }}
      </v-card-title>

      <v-divider />

      <v-card-text class="pa-6">
        <v-alert type="warning" class="mb-4">
          {{ $t('auth.temporaryPasswordWarning') }}
        </v-alert>

        <v-form @submit.prevent="changePassword">
          <v-text-field
            v-model="newPassword"
            :label="$t('auth.newPassword')"
            :rules="passwordRules"
            :type="showPassword ? 'text' : 'password'"
            variant="outlined"
            prepend-inner-icon="mdi-lock"
            :append-inner-icon="showPassword ? 'mdi-eye-off' : 'mdi-eye'"
            class="mb-4"
            @click:append-inner="showPassword = !showPassword"
          />

          <v-text-field
            v-model="confirmPassword"
            :label="$t('auth.confirmPassword')"
            :rules="confirmPasswordRules"
            :type="showConfirmPassword ? 'text' : 'password'"
            variant="outlined"
            prepend-inner-icon="mdi-lock-check"
            :append-inner-icon="showConfirmPassword ? 'mdi-eye-off' : 'mdi-eye'"
            class="mb-4"
            @click:append-inner="showConfirmPassword = !showConfirmPassword"
          />

          <v-btn
            type="submit"
            color="primary"
            size="large"
            block
            :loading="loading"
            class="mb-3"
          >
            {{ $t('auth.updatePassword') }}
          </v-btn>
        </v-form>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script setup>
import { ref, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useAuthStore } from '@/stores/auth'
import { useSnackbarStore } from '@/stores/snackbar'
import api from '@/utils/api'

const { t } = useI18n()
const authStore = useAuthStore()
const snackbarStore = useSnackbarStore()

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['update:modelValue', 'password-changed'])

const show = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value)
})

const newPassword = ref('')
const confirmPassword = ref('')
const showPassword = ref(false)
const showConfirmPassword = ref(false)
const loading = ref(false)

const passwordRules = [
  v => !!v || t('validation.required'),
  v => v.length >= 8 || t('validation.minLength', { min: 8 })
]

const confirmPasswordRules = [
  v => !!v || t('validation.required'),
  v => v === newPassword.value || t('validation.passwordMatch')
]

const changePassword = async () => {
  if (!newPassword.value || newPassword.value !== confirmPassword.value) {
    return
  }

  loading.value = true

  try {
    await api.post('/auth/change-password', {
      phone: authStore.user.phone,
      newPassword: newPassword.value
    })

    snackbarStore.show(t('auth.passwordChanged'), 'success')
    emit('password-changed')
    show.value = false
    
    // Clear form
    newPassword.value = ''
    confirmPassword.value = ''
  } catch (error) {
    snackbarStore.show(error.response?.data?.error || t('auth.passwordChangeError'), 'error')
  } finally {
    loading.value = false
  }
}
</script>